import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLeadDialog } from "@/components/site/LeadDialog";
import { PORTFOLIO } from "@/lib/constants";
import LaptopCanvas from "./LaptopCanvas";
import { useDrivenStyle, mapRange } from "./timeline";

/**
 * MobileExperience - simplified touch-friendly version of the cinematic.
 * Hero → short pinned laptop open → tap-through portfolio → CTA.
 */

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.1 },
  }),
};

export default function MobileExperience() {
  const { openPreview } = useLeadDialog();
  const stageRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: stageRef,
    offset: ["start start", "end end"],
  });
  const heroRef = useDrivenStyle(scrollYProgress, (el, p) => {
    el.style.opacity = String(mapRange(p, 0, 0.35, 1, 0));
    el.style.transform = `translateY(${mapRange(p, 0, 0.35, 0, -40)}px)`;
  });

  return (
    <div data-testid="mobile-experience">
      {/* Hero + opening laptop (short pinned scene) */}
      <section ref={stageRef} className="relative h-[260vh] bg-[color:var(--ww-cream)]">
        <div className="sticky top-0 h-[100svh] overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[52%] z-10">
            <div
              ref={heroRef}
              className="h-full px-5 pt-24 flex flex-col"
            >
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-medium text-black/55 font-body">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Studio · Ireland
              </span>
              <h1
                data-testid="hero-headline"
                className="mt-4 font-display font-bold uppercase text-[clamp(2rem,9.5vw,3.4rem)] leading-[0.97] tracking-tight text-[color:var(--ww-charcoal)]"
              >
                <span className="block overflow-hidden pb-0.5">
                  <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                    High-performing
                  </motion.span>
                </span>
                <span className="block overflow-hidden pb-0.5">
                  <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-brand-gradient">
                    websites &amp; software
                  </motion.span>
                </span>
                <span className="block overflow-hidden pb-0.5">
                  <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">
                    for service businesses.
                  </motion.span>
                </span>
              </h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.7 }}
                className="mt-4 text-sm text-black/60 leading-relaxed font-body max-w-sm"
              >
                Designed and built in Ireland. See the laptop open as you scroll.
              </motion.p>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 h-[62%]">
            <LaptopCanvas progress={scrollYProgress} mode="mobile" />
          </div>
        </div>
      </section>

      {/* Our Work - tap-through */}
      <section id="work" className="relative bg-[color:var(--ww-cream)] px-5 pb-20 pt-4">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-bold uppercase text-4xl tracking-tight text-[color:var(--ww-charcoal)]">
            Our <span className="text-brand-gradient">work.</span>
          </h2>
          <span className="font-mono text-[10px] text-black/40">6 projects</span>
        </div>
        <div className="mt-6 space-y-5">
          {PORTFOLIO.map((p) => (
            <a
              key={p.slug}
              href={p.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`mobile-project-${p.slug}`}
              className="block rounded-2xl overflow-hidden border border-black/10 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
            >
              <div className="h-44 overflow-hidden">
                <img
                  src={p.screenshot}
                  alt={p.name}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="flex items-center justify-between px-4 py-3.5">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.22em] text-emerald-700 font-semibold font-body">
                    {p.label}
                  </p>
                  <p className="font-display font-bold text-lg tracking-tight text-[color:var(--ww-charcoal)]">
                    {p.name}
                  </p>
                </div>
                <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 text-black">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-14 text-center">
          <h3 className="font-display font-bold uppercase text-4xl leading-[0.96] tracking-tight text-[color:var(--ww-charcoal)]">
            Your business<br />
            <span className="text-brand-gradient">could be next.</span>
          </h3>
          <p className="mt-4 text-sm text-black/55 font-body max-w-xs mx-auto">
            See what your new website could look like before you pay anything.
          </p>
          <button
            type="button"
            onClick={() => openPreview()}
            data-testid="mobile-cta-free-preview"
            className="mt-7 inline-flex items-center gap-3 pl-5 pr-2 py-2 rounded-full bg-[color:var(--ww-charcoal)] text-white text-sm font-semibold font-body"
          >
            Get Your Free Website Preview
            <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 text-black">
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </button>
        </div>
      </section>
    </div>
  );
}
